import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Phase 3.1 Tier 2 — listing detail interactions.
//
// Drives:
//   * Connect-button placeholder (real handoff lands in Tier 4 with
//     the OAuth handler abstraction).
//   * Notify-me waitlist modal: open / close / submit + toast.
//
// All flash messages are styled toasts via the shared `flash`
// controller's CSS classes — no `alert()`, no `confirm()`.
export default class extends Controller {
  static targets = [
    "waitlistModal", "waitlistForm", "waitlistSubmit",
    "apiKeyModal", "apiKeyForm", "apiKeySubmit",
    "netsuiteModal", "bcModal", "sapModal", "oracleModal",
    // § 3.11 Tier 1 — MRI Software's 6-input pre-OAuth modal.
    "mriModal",
    // § 3.12 Tier 2 — Fieldwire's 2-input pre-OAuth modal (api_token + region).
    "fieldwireModal",
    "dronedeployModal",
    // § 3.14 Tier 2 — DocuSign's sandbox-vs-production pre-OAuth modal.
    "docusignModal",
    // § 3.17 Tier 2 — Zoom's 5-input Server-to-Server OAuth modal
    // (Account ID + Client ID + Client Secret + Webhook Secret +
    // scheduled-host email).
    "zoomModal",
    // § 3.19 Tier 2 — Trello's single-input paste-the-token modal.
    "trelloModal"
  ]
  static values = { providerSlug: String }

  // ── Connect (Tier 2 stub — preserved for back-compat). Tier 4
  // ships the real handoff via the connect_button Stimulus controller
  // (no-OAuth path goes through openApiKeyConnect below).

  connectStub(event) {
    event.preventDefault()
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      lifecycle: "available"
    })
    this._showToast(
      "Connecting will go live with the OAuth flow in an upcoming release. We've logged your interest.",
      "notice"
    )
  }

  // ── API-key connect modal (Generic Webhook URL) ─────────────────
  openApiKeyConnect() {
    if (!this.hasApiKeyModalTarget) return
    this.apiKeyModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key"
    })
  }

  closeApiKeyConnect() {
    if (!this.hasApiKeyModalTarget) return
    this.apiKeyModalTarget.classList.add("hidden")
  }

  async submitApiKeyConnect(event) {
    event.preventDefault()
    if (!this.hasApiKeyFormTarget) return
    const form = this.apiKeyFormTarget
    const submit = this.hasApiKeySubmitTarget ? this.apiKeySubmitTarget : null
    const originalLabel = submit ? submit.value : null
    if (submit) {
      submit.disabled = true
      submit.value = "Connecting…"
    }

    try {
      const formData = new FormData(form)
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const headers = { Accept: "application/json" }
      if (csrfMeta) headers["X-CSRF-Token"] = csrfMeta.content
      const res = await fetch(form.action, {
        method: "POST",
        body: formData,
        headers,
        credentials: "same-origin"
      })
      const payload = await res.json().catch(() => ({}))
      if (res.ok && payload.ok) {
        this._showToast("Connected.", "notice")
        if (payload.redirect_url) {
          window.location.href = payload.redirect_url
          return
        }
      } else {
        this._showToast(payload.message || "Couldn't save the connection.", "alert")
      }
    } catch (e) {
      this._showToast("Network error — please try again.", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        if (originalLabel) submit.value = originalLabel
      }
    }
  }

  // ── NetSuite pre-OAuth account-id capture modal (§ 3.9 Tier 2) ──
  openNetsuiteConnect() {
    if (!this.hasNetsuiteModalTarget) return
    this.netsuiteModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "oauth2_authcode",
      pre_oauth_form: "netsuite_account_id"
    })
  }

  closeNetsuiteConnect() {
    if (!this.hasNetsuiteModalTarget) return
    this.netsuiteModalTarget.classList.add("hidden")
  }

  // ── Dynamics 365 BC pre-OAuth tenant + environment modal (§ 3.9 Tier 4) ─
  openBcConnect() {
    if (!this.hasBcModalTarget) return
    this.bcModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "oauth2_authcode",
      pre_oauth_form: "bc_tenant_environment"
    })
  }

  closeBcConnect() {
    if (!this.hasBcModalTarget) return
    this.bcModalTarget.classList.add("hidden")
  }

  // ── SAP S/4HANA Cloud pre-OAuth credentials modal (§ 3.10 Tier 1) ─
  // Public Edition exposes only `oauth2_client_credentials` — there is
  // no consent screen, so the modal collects all four credentials
  // (token URL + service URL + client_id + client_secret) plus the
  // sandbox toggle and `start_oauth` mints the first access token
  // server-side before redirecting to /connections/:id.
  openSapConnect() {
    if (!this.hasSapModalTarget) return
    this.sapModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "oauth2_client_credentials",
      pre_oauth_form: "sap_communication_arrangement"
    })
  }

  closeSapConnect() {
    if (!this.hasSapModalTarget) return
    this.sapModalTarget.classList.add("hidden")
  }

  // ── Oracle Fusion ERP Cloud pre-OAuth credentials modal (§ 3.10 Tier 1) ─
  // Authorization Code via OCI IAM — the modal collects IDCS host + FA
  // base URL + client_id + client_secret (the four values needed to
  // resolve `{IDCS_HOST}` / `{FA_BASE_URL}` substitutions on the
  // authorize URL + token URL + scopes), then redirects to Oracle's
  // consent screen on submit.
  openOracleConnect() {
    if (!this.hasOracleModalTarget) return
    this.oracleModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "oauth2_authcode",
      pre_oauth_form: "oracle_idcs_credentials"
    })
  }

  closeOracleConnect() {
    if (!this.hasOracleModalTarget) return
    this.oracleModalTarget.classList.add("hidden")
  }

  // ── MRI Software pre-OAuth credentials modal (§ 3.11 Tier 1) ─────
  // 6-input Basic-auth form (PartnerKey + ClientID + Database +
  // WebServicesUser + Password + WebDomain). Tier 3 wires the
  // `IntegrationOAuthService.bootstrap_basic_auth!` symmetric counterpart
  // to `bootstrap_client_credentials!` — the controller assembles the
  // composite Base64 username `<ClientID>/<Database>/<WebServicesUser>/<PartnerKey>:<Password>`,
  // persists as the encrypted `IntegrationConnection.api_key`, calls
  // `MriSoftware::Adapter#connect` for tenant verification, and
  // redirects to the connection-detail page (no third-party consent
  // screen — MIX uses long-lived Basic auth credentials).
  openMriConnect() {
    if (!this.hasMriModalTarget) return
    this.mriModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key",
      pre_oauth_form: "mri_basic_auth_credentials"
    })
  }

  closeMriConnect() {
    if (!this.hasMriModalTarget) return
    this.mriModalTarget.classList.add("hidden")
  }

  // ── Fieldwire pre-OAuth credentials modal (§ 3.12 Tier 2) ────────
  // 2-input api_token + region modal. The token mints a JWT
  // server-side via `IntegrationOAuthService.bootstrap_fieldwire_api_key!`
  // (which POSTs `/api_keys/jwt`, GETs `/account` for identity, and
  // persists the refresh as encrypted `IntegrationConnection.api_key`),
  // then redirects to the connection-detail page in one round-trip
  // (no consent screen — Fieldwire's API token is long-lived).
  // ── DroneDeploy API-key modal (Site Captures) ────────────────────
  openDronedeployConnect() {
    if (!this.hasDronedeployModalTarget) return
    this.dronedeployModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key",
      pre_oauth_form: "dronedeploy_api_key"
    })
  }

  closeDronedeployConnect() {
    if (!this.hasDronedeployModalTarget) return
    this.dronedeployModalTarget.classList.add("hidden")
  }

  openFieldwireConnect() {
    if (!this.hasFieldwireModalTarget) return
    this.fieldwireModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key",
      pre_oauth_form: "fieldwire_api_token_region"
    })
  }

  closeFieldwireConnect() {
    if (!this.hasFieldwireModalTarget) return
    this.fieldwireModalTarget.classList.add("hidden")
  }

  // ── Zoom pre-OAuth credentials modal (§ 3.17 Tier 2) ─────────────
  // 5-input Server-to-Server OAuth form. The S2S grant is a paste-
  // credential-style API-key flow: customer creates one S2S app
  // inside their Zoom account, pastes Account ID + Client ID +
  // Client Secret + Webhook Secret + scheduled-host email, and
  // `bootstrap_zoom_s2s!` mints a token + verifies the host +
  // persists the credentials encrypted at rest in one round-trip
  // (no consent screen).
  openZoomConnect() {
    if (!this.hasZoomModalTarget) return
    this.zoomModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key",
      pre_oauth_form: "zoom_s2s_credentials"
    })
  }

  closeZoomConnect() {
    if (!this.hasZoomModalTarget) return
    this.zoomModalTarget.classList.add("hidden")
  }

  // ── Trello paste-the-token modal (§ 3.19 Tier 2) ─────────────────
  // Single-input paste-the-token form. The user opens the Trello
  // consent screen at `https://trello.com/1/authorize` in a new tab,
  // copies the returned token, and pastes it back. `bootstrap_trello_api_key!`
  // validates via `GET /1/tokens/<token>` + `GET /1/members/me`,
  // persists the encrypted token on `IntegrationConnection.api_key`,
  // and lands on the connection-detail page in one round-trip
  // (no consent screen on our side — Trello hosts it).
  openTrelloConnect() {
    if (!this.hasTrelloModalTarget) return
    this.trelloModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "api_key",
      pre_oauth_form: "trello_user_token"
    })
  }

  closeTrelloConnect() {
    if (!this.hasTrelloModalTarget) return
    this.trelloModalTarget.classList.add("hidden")
  }

  // ── DocuSign pre-OAuth sandbox-vs-prod modal (§ 3.14 Tier 2) ────
  // DocuSign's authorize / token / userinfo URLs all live on
  // `account.docusign.com` (production) or `account-d.docusign.com`
  // (sandbox / developer); the modal collects which one to redirect
  // to before the OAuth handoff. The integration key the customer
  // pasted into Apps & Keys is bound to one or the other — wrong
  // pick fails at consent. Production is the default; the radio +
  // an optional intended-account-id capture for the re-auth path.
  openDocusignConnect() {
    if (!this.hasDocusignModalTarget) return
    this.docusignModalTarget.classList.remove("hidden")
    track("Integration Connect Clicked", {
      provider_slug: this.providerSlugValue,
      auth_kind: "oauth2_authcode",
      pre_oauth_form: "docusign_environment"
    })
  }

  closeDocusignConnect() {
    if (!this.hasDocusignModalTarget) return
    this.docusignModalTarget.classList.add("hidden")
  }

  // ── Waitlist modal ─────────────────────────────────────────────

  openWaitlist() {
    if (!this.hasWaitlistModalTarget) return
    this.waitlistModalTarget.classList.remove("hidden")
    track("Integration Waitlist Opened", {
      provider_slug: this.providerSlugValue
    })
  }

  closeWaitlist() {
    if (!this.hasWaitlistModalTarget) return
    this.waitlistModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submitWaitlist(event) {
    event.preventDefault()
    if (!this.hasWaitlistFormTarget) return
    const form = this.waitlistFormTarget
    const submit = this.hasWaitlistSubmitTarget ? this.waitlistSubmitTarget : null
    const originalLabel = submit ? submit.value : null

    if (submit) {
      submit.disabled = true
      submit.value = "Submitting…"
    }

    try {
      const formData = new FormData(form)
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const headers = { Accept: "application/json" }
      if (csrfMeta) headers["X-CSRF-Token"] = csrfMeta.content

      const res = await fetch(form.action, {
        method: "POST",
        body: formData,
        headers,
        credentials: "same-origin"
      })

      let payload = null
      try { payload = await res.json() } catch (_e) { payload = null }

      if (res.ok && payload && payload.ok) {
        this.closeWaitlist()
        this._showToast(payload.message || "You're on the waitlist.", "notice")
        track("Integration Waitlisted", {
          provider_slug: this.providerSlugValue
        })
        // Re-render the hero CTA on next nav; for now flip the button
        // to "On waitlist" inline so the user sees the new state.
        this._swapHeroToOnWaitlist()
      } else {
        const message =
          (payload && payload.message) ||
          "We couldn't add you to the waitlist. Please try again."
        this._showToast(message, "alert")
      }
    } catch (e) {
      this._showToast("Network error — please try again.", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        if (originalLabel) submit.value = originalLabel
      }
    }
  }

  // ── Internals ──────────────────────────────────────────────────

  _swapHeroToOnWaitlist() {
    // The hero CTA is a button; replace it with the static success
    // pill so the user doesn't end up able to submit twice.
    const hero = document.querySelector('button[data-action*="openWaitlist"]')
    if (!hero) return
    const pill = document.createElement("span")
    pill.className =
      "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[13px] font-medium"
    pill.textContent = "You're on the waitlist"
    hero.replaceWith(pill)
  }

  _showToast(message, type) {
    const toast = document.createElement("div")
    const cls = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.className = cls
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}
